<template>
  <div
    v-if="plan && plan.merchant_id"
    class="plan-bundle-list-item"
    :class="[price.cents && 'plan-bundle-list-item--cents']"
    @click="updateSelectedPlan(plan)"
    :id="cardIdValue"
  >
    <div class="plan-bundle-list-item__badge-wrapper">
      <img
        :src="plan.badge"
        :alt="plan.name"
        :class="[
          'plan-bundle-list-item__badge-img',
          `plan-bundle-list-item__badge-img--${plan.merchant_id}`,
        ]"
      />
    </div>
    <div class="plan-bundle-list-item__price">
      {{ `$${price.units}` }}
      <span v-if="price.cents" :class="['plan-bundle-list-item__price--cents']">{{
        price.cents
      }}</span>
      <div v-if="isBlackFridayPage" class="plan-bundle-list-item__label-discount">
        <div class="plan-bundle-list-item__label-discount-50">50% OFF WITH CODE:</div>
        <div class="plan-bundle-list-item__label-discount-coupon">CMCERTPLUS24</div>
        <div class="plan-bundle-list-item__label-discount-text">Discounted prices are shown</div>
      </div>
    </div>
    <div class="plan-bundle-list-item__radio-button">
      <RadioButton :isSelected="selected" />
    </div>
    <div class="plan-bundle-list-item__name-wrapper">
      <h4 class="plan-bundle-list-item__name">
        {{ plan.nameShort }}
        <span class="plan-bundle-list-item__name--promo-info">{{ plan.promoInfo.title }}</span>
      </h4>
      <div class="plan-bundle-list-item__promo-info">
        {{ plan.promoInfoExtra }}
      </div>
    </div>
    <div class="plan-bundle-list-item__description">
      {{ plan.description }}
    </div>
    <div v-if="!isAuthenticated" class="plan-bundle-list-item__no-auth">
      <div class="plan-bundle-list-item__presale-container" v-if="plan.onPreSale">
        <div class="plan-bundle-list-item__presale-offer-tag">
          <div class="plan-bundle-list-item__presale-offer-tag-title">BUY NOW AND SAVE</div>
          <div class="plan-bundle-list-item__presale-offer-tag-value">$200 OFF</div>
        </div>
        <div class="plan-bundle-list-item__presale-text">
          <div class="plan-bundle-list-item__presale-description">
            PRE-SALE SAVINGS FOR NEW SUBSCRIBERS.
          </div>
          <div class="plan-bundle-list-item__presale-full-price">Full price $599</div>
        </div>
      </div>
      <template v-else>
        <span class="plan-bundle-list-item__no-auth--bold">
          {{ `Already have ${plan.alreadyINE} subscription?` }}
        </span>
        <span
          class="plan-bundle-list-item__no-auth-cta plan-bundle-list-item__no-auth-cta--color-purple"
          @click="handleOpenModal"
        >
          Sign in to your account
        </span>
        to get the standalone certification.
      </template>
    </div>
    <div class="plan-bundle-list-item__best-value">
      <strong>BUNDLE&nbsp;</strong>{{ plan.bundleShortInfo }}
    </div>
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex'
import { updateCart, routesRules } from '@/helpers/mixins'
import CONSTANTS from '@/constants'
import EJPTBadge from '@/assets/icons/certs/ejpt-badge.png'
import EEDABadge from '@/assets/icons/certs/eeda-badge.png'
import EWPTBadge from '@/assets/icons/certs/ewpt-badge.png'
import RadioButton from '@/components/shared/RadioButton.vue'
import ECPPTBadge from '@/assets/icons/certs/eCPPT-badge-small.png'
import EWPTXBadge from '@/assets/icons/certs/ewptx_badge.png'

export default {
  name: 'PlanBundleItem',
  mixins: [updateCart, routesRules],
  components: {
    RadioButton,
  },
  props: {
    plan: {
      type: Object,
      required: true,
      default: () => {
        return {}
      },
    },
    cardId: {
      type: String,
      required: false,
    },
  },
  computed: {
    ...mapState({
      isAuthenticated: state => state.auth.isAuthenticated,
    }),
    selected() {
      return this.plan.merchant_id === this.selectedPlan.merchant_id
    },
    price() {
      const { price_cents, discounted_price_cents, user_receives_discount } = this.plan
      let price = user_receives_discount ? discounted_price_cents : price_cents

      // remove this division by 200 after CM
      price = this.isBlackFridayPage ? price / 200 : price / 100

      const splitPrice = String(price).split('.')
      return {
        fullPrice: `$${price}`,
        units: splitPrice[0],
        cents:
          splitPrice[1] > 0 ? `.${splitPrice[1] < 10 ? splitPrice[1] * 10 : splitPrice[1]}` : false,
      }
    },
    cardIdValue() {
      return this.cardId || this.plan.merchant_id
    },
  },
  created() {
    const {
      PLANS: {
        MONTHLY_EJPT,
        EJPT_FUNDAMENTALS,
        EDA_PREMIUM,
        EWPT_PREMIUM,
        EWPTX_PREMIUM,
        ECPPT_PREMIUM,
      },
    } = CONSTANTS
    switch (this.plan.merchant_id) {
      case MONTHLY_EJPT:
      case EJPT_FUNDAMENTALS:
        this.plan.badge = EJPTBadge
        break
      case EDA_PREMIUM:
        this.plan.badge = EEDABadge
        break
      case EWPT_PREMIUM:
        this.plan.badge = EWPTBadge
        break
      case ECPPT_PREMIUM:
        this.plan.badge = ECPPTBadge
        break
      case EWPTX_PREMIUM:
        this.plan.badge = EWPTXBadge
        break
      default:
        break
    }
  },
  methods: {
    ...mapMutations({
      setIsSignInModalShown: 'app/IS_SIGN_IN_MODAL_SHOWN_UPDATED',
    }),
    handleOpenModal() {
      this.setIsSignInModalShown(true)
    },
  },
}
</script>

<style lang="scss" scoped>
.plan-bundle-list-item {
  $this: &;
  padding: 40px 20px;
  border-radius: 10px 10px 0px 0px;
  background: var(--mid-gray-gradient);
  cursor: pointer;
  display: grid;
  grid-template-columns: repeat(2, 1fr) 50px;
  row-gap: 20px;
  position: relative;
  &:hover {
    background: var(--mid-gray-translucid-gradient);
  }
  &:active {
    transition: all 0.5s ease-out;
    background: var(--charcoal-gradient-two);
  }
  @include mobile {
    grid-template-columns: 110px auto 130px 30px;
    column-gap: 20px;
    &--cents {
      grid-template-columns: 110px auto 130px 50px;
    }
  }
  @include desktop {
    grid-template-columns: 130px auto 130px 30px;
    padding: 40px;
    &--cents {
      grid-template-columns: 130px auto 130px 50px;
    }
  }
  &__badge {
    &-wrapper {
      @include mobile {
        grid-area: 1/1/5/2;
      }
    }
    &-img {
      width: 110px;
      @include mobile {
        margin-top: 5px;
      }
      @include desktop {
        width: 130px;
      }
    }
  }
  &__price {
    text-align: right;
    font-size: rem(54px);
    font-weight: 700;
    line-height: rem(66px);
    grid-area: 1/2;
    @include mobile {
      grid-area: 1/3/2/4;
      font-size: rem(54px);
      line-height: rem(66px);
    }
    @include desktop {
      position: relative;
      .plan-bundle-list-item--cents & {
        position: relative;
        width: 160px;
        text-align: left;
      }
    }
    &--cents {
      font-size: rem(27px);
      position: absolute;
      top: -10px;
      &-small-tile {
        font-size: rem(20px);
        font-style: normal;
        font-weight: 700;
        line-height: rem(20px);
        top: 0;
      }
    }
  }
  &__radio-button {
    grid-area: 1/3/2/4;
    margin-top: 15px;
    @include mobile {
      grid-area: 1/4/2/5;
      display: flex;
      margin: 0;
      justify-content: flex-end;
      align-items: center;
    }
  }
  &__name {
    &-wrapper {
      grid-area: 2/1/3/4;
      @include mobile {
        grid-area: 1/2/2/3;
        display: flex;
        flex-flow: column;
        gap: 5px;
        width: 80%;
        justify-content: center;
      }
    }
    font-size: rem(18px);
    font-weight: 500;
    line-height: rem(24px);
    margin: 0;
    font-family: var(--font-bold);
    &--promo-info {
      color: var(--purple-pale);
      font-size: rem(18px);
      font-weight: 500;
      line-height: rem(24px);
    }
  }
  &__promo-info {
    color: var(--dusty-gray);
    font-size: rem(12px);
    font-weight: 450;
    line-height: rem(14px);
  }
  &__description {
    grid-area: 3/1/4/4;

    color: var(--white);

    font-family: var(--font-regular);
    font-size: 0.875rem;
    font-style: normal;
    font-weight: 450;
    line-height: 1.25rem; /* 142.857% */
    @include mobile {
      grid-area: 2/2/3/5;
    }
    @include desktop {
      grid-area: 2/2/3/3;
      min-height: 80px;
    }
  }
  &__no-auth {
    grid-area: 4/1/5/4;
    margin-bottom: 30px;
    @include mobile {
      grid-area: 3/2/4/5;
      margin-bottom: 10px;
    }
  }
  &__no-auth {
    font-size: rem(14px);
    font-weight: 450;
    line-height: rem(20px);
    &--bold {
      font-weight: 700;
    }
    &-cta {
      text-decoration: underline;
      &--color-purple {
        color: var(--purple-pale);
      }
    }
  }
  &__presale {
    &-container {
      display: flex;
      gap: 20px;
      align-items: center;
    }
    &-offer-tag {
      border-radius: 3px;
      border: 1px solid var(--strong-light-green-b5);
      background: var(--black-2);
      box-shadow: 4px 4px 17px 0px rgba(0, 0, 0, 0.65);

      display: flex;
      padding: 7px 5px;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 4px;

      &-title {
        color: var(--white);
        text-align: center;
        font-family: var(--font-bold);
        font-size: 0.625rem;
        font-style: normal;
        font-weight: 700;
        line-height: 0.625rem; /* 100% */
      }
      &-value {
        color: var(--strong-light-green-b5);
        text-align: center;
        font-feature-settings: 'clig' off, 'liga' off;
        font-family: var(--font-bold);
        font-size: 1.375rem;
        font-style: normal;
        font-weight: 900;
        line-height: 1.25rem; /* 90.909% */
      }
    }
    &-text {
      display: flex;
      flex-direction: column;
      gap: 5px;
    }
    &-description {
      color: var(--strong-light-green-b5);
      font-family: var(--font-bold);
      font-size: 0.9375rem;
      font-style: normal;
      font-weight: 700;
      line-height: 0.875rem; /* 93.333% */
    }
    &-full-price {
      color: var(--white);

      font-family: var(--font-regular);
      font-size: 0.75rem;
      font-style: normal;
      font-weight: 450;
      line-height: 0.875rem; /* 116.667% */
    }
  }
  &__best-value {
    grid-area: 5/1/6/4;
    background: var(--purple-gradient);
    position: absolute;
    top: 10px;
    right: -20px;
    width: calc(100% + 40px);
    border-radius: 0 0 10px 10px;
    height: 35px;
    display: flex;
    justify-content: center;
    align-items: center;

    color: var(--dusty-gray);
    font-size: rem(12px);
    font-weight: 450;
    line-height: rem(14px);
    @include mobile {
      grid-area: 4/1/5/5;
    }
    @include desktop {
      width: calc(100% + 80px);
      right: -40px;
    }
  }
  &__label-discount {
    color: var(--strong-light-green);
    text-align: center;
    font-size: rem(14px);
    font-style: normal;
    line-height: rem(20px);
    &-50 {
      font-weight: 450;
    }
    &-coupon {
      font-weight: 700;
    }
    &-text {
      color: var(--light-blue-gray);
      font-size: rem(12px);
      font-weight: 450;
      line-height: rem(20px);
    }
  }
}
</style>
